import { DocType, LeasingDocs } from '@/models/docs.js';

import {
    Doc,
    OcrProgress,
    ProgressPhase,
    RawOcrProgress,
    computeOcrProgress,
} from '@/lib/types';

import { DBFile, DBIngestedFile, FileType } from '../file-overview/types';

export { computeOcrProgress };
export type { OcrProgress, ProgressPhase, RawOcrProgress };

/** Shared Status-Hierarchy AI Insight enum for HT, AD, and future status reports. */
export type StatusReportAIInsight =
    | 'missing_information'
    | 'issues_detected'
    | 'rejected'
    | 'ready_for_review'
    | 'in_progress'
    | 'approved'
    | 'not_pertinent';

export interface IngestionWarning {
    warningType: string;
    message: string;
    detectedIndicators: string[];
    suggestedType: string | null;
}

export enum DocumentStatus {
    pending = 'pending',
    processing_ocr = 'processing_ocr',
    processing_compliance = 'processing_compliance',
    ai_verified = 'ai_verified',
    requires_attention = 'requires_attention',
    verified = 'verified',
    ground_truth = 'ground_truth',
}

export enum ComponentStatus {
    pending = 'pending',
    requires_attention = 'requires_attention',
    missing_documents = 'missing_documents',
    arc_missing = 'arc_missing',
    serial_mismatch = 'serial_mismatch',
    verified = 'verified',
}

export type ComponentCategory = 'H000' | 'H001' | 'H002';

/** `GET /api/components/`'s `sort`: `<column>_<asc|desc>`. Status has none: its badge is worked out per row. */
export type ComponentSort = `${
    | 'ata'
    | 'mpd_reference'
    | 'nomenclature'
    | 'work_description'
    | 'part_number'
    | 'serial_number'
    | 'position'}_${'asc' | 'desc'}`;

export type AviationDocumentType =
    | 'AircraftInspectionReport'
    | 'MaintenancePlanningDocument'
    | 'StatusListHardTimeComponents'
    | 'AuthorizedReleaseCertificate'
    | 'StatusReport'
    | 'WorkOrder'
    | 'EngineeringOrder'
    | 'TaskCard'
    | 'ServiceBulletin'
    | 'AirworthinessDirective'
    | 'ShippingManifest'
    | 'AirworthinessDirectivesAirframeAppliance'
    | 'ServiceBulletinsAirframeVendors'
    | 'FlammabilityCertifications'
    | 'STCsMajorModEO'
    | 'V16207'
    | 'V16215'
    | 'StructuralRepairRecords'
    | 'TimeControlledComponents'
    | 'OnConditionComponents'
    | 'CPCPs'
    | 'AircraftPhysicalDiscrepancies'
    | 'CrewSeatsPaxSeats'
    | 'APU'
    | 'LDGs'
    | 'MiscellaneousDocumentation'
    | 'MPDTasks'
    | 'PreviousCheckRecords'
    | 'RedeliveryCheckManagement'
    | 'NonRoutineMaintenanceSheet'
    | 'UtilizationReport'
    | 'LastDoneNextDue'
    | 'AircraftDeliveryTechLogbook';

export type AviationDocumentCategory =
    | 'Certificates'
    | 'MaintenanceStatusSummaries'
    | 'AircraftMaintenanceRecords'
    | 'ConfigurationStatus'
    | 'AircraftManufacturerRecords'
    | 'EngineRecords_1'
    | 'EngineRecords_2'
    | 'APURecords'
    | 'ComponentRecords'
    | 'LandingGearRecords'
    | 'Manuals'
    | 'Miscellaneous';

interface TypedValue {
    value: string;
    type: string;
}

interface TypedBool {
    value: boolean;
    type: string;
}

interface TypedNumber {
    value: number;
    type: string;
}

interface TypedDate {
    value: string;
    type: string;
}

interface TypedLocalizedString {
    value: string;
    type: string;
    language: string;
}

export interface Aircraft {
    type: string;
    registration: string;
    serialNumber: string;
}

export interface Reference {
    referenceType: string;
    referenceId: string;
    description: string | null;
}

interface Compliance {
    category: string;
    timeframe: string;
}

interface Step {
    stepNumber: string;
    instruction: string;
    completed: boolean;
}

interface SignedOff {
    value: boolean;
    type: string;
    by: string;
    date: string;
}

export interface Task {
    taskId: string;
    description: string;
    steps: Step[];
    signedOff: SignedOff | null;
}

export interface Approval {
    role: string;
    name: string;
    signed: boolean;
    date: string;
}

export type AviationDocument = {
    type: AviationDocumentType;
    // Null when the model could not read the document's own identifier off the
    // page. The type is still set, so the document keeps its category.
    id: string | null;
    title: string | null;
    issueDate: string;
    effectiveDate: string;
    expiryDate: string | null;
    aircraft: Aircraft;
    estimatedManHours: number | null;
    actualManHours: number | null;
    references: Reference[];
    priority: string;
    compliance: Compliance;
    issuingAuthority: string;
    tasks: Task[];
    approvals: Approval[];
};

export type ConnectedDocument = {
    id: number;
    type: DocType;
    source: boolean;
    signed: boolean;
};

export interface ADHeader {
    issuingAuthority?: string;
    adNumber: string;
    issueDate?: string;
    designApprovalHolderName?: string;
    typeOrModelDesignation?: string;
    effectiveDate?: string;
    tcdsNumber?: string;
    supersedure?: string;
    ataChapter?: number;
    ataChapterTitle?: string;
}

export interface ADContent {
    manufacturer?: string;
    applicability?: string;
    definition?: string;
    reason?: string;
    requiredActionsAndComplianceTimes?: string;
    contactInfo?: string;
}

export interface ADReference {
    type: string;
    id: string;
    revision?: string;
    revision_date?: string;
}

export interface AirworthinessDirective extends ADHeader, ADContent {
    references: ADReference[];
    statusClosed?: boolean;
}

export interface EngineeringOrder {
    id: string;
    subject: string;
    summary?: string | null;
    classification?: string | null;
    reasonForRevision?: string | null;
    planningInformation?: string | null;

    // Aircraft Information
    registration?: string | null;
    type?: string | null;
    sN?: string | null;
    enginesType?: string | null;
    fH?: number | null;
    fC?: number | null;

    // References
    damageReportRefs: string[];
    doaDocumentsRefs: string[];

    createdBy?: string | null;
    issuedBy?: string | null;
}

export type DocumentHolder = {
    id: number;
    type: string;
};

export enum ProjectStatus {
    InProgress = 'in_progress',
    Done = 'done',
}

export enum LeasingAircraftStatus {
    active = 'active',
    maintenance = 'maintenance',
}

export type LeasingAircraft = {
    msn: string;
    type: string;
    registration: string;
    operator: string | null;
    status: LeasingAircraftStatus;
    manufacturer: string;
};

export interface OperatingTime {
    designation: string;
    serialNo: string;
    totalFH: string;
    totalFC: string;
}

interface AircraftEngine {
    id: number;
    position: string;
    manufacturer: string;
    family: string;
    dashVariant: string;
    serialNumber: string;
    statusDate?: string | null;
    timeSinceNew?: string | null;
    cyclesSinceNew?: number | null;
    installDate?: string | null;
}

interface AircraftApu {
    id: number;
    manufacturer: string;
    typeAndModel: string;
    serialNumber: string;
    statusDate?: string | null;
    timeSinceNew?: string | null;
    cyclesSinceNew?: number | null;
    installDate?: string | null;
}

interface AircraftLandingGearEntry {
    id: number;
    position: string;
    manufacturer: string;
    partNumber: string;
    serialNumber: string;
    statusDate?: string | null;
    timeSinceNew?: string | null;
    cyclesSinceNew?: number | null;
}

export interface AircraftSchema {
    id: number;
    teamId: number;
    createdAt: string;
    updatedAt: string;
    msn: string;
    registration: string;
    type: string;
    variant?: string | null;
    manufacturer?: string | null;
    operator?: string | null;
    engineType?: string | null;
    tsn?: string | null;
    csn?: string | null;
    manufacturerYear?: string | null;
    manufactureDate?: string | null;
    // Aircraft category (Passenger/Freighter/Special Freighter (P2F)/Combi) —
    // confusingly, the Create Asset redesign form's "Aircraft type" field maps
    // to this, not to `type` (which holds the aircraft *model*, e.g. "A320").
    aircraftCategory?: string | null;
    lineNumber?: string | null;
    airframeStatusDate?: string | null;
    avgDailyUtilizationFh?: string | null;
    avgDailyUtilizationFc?: number | null;
    // Landing gear assembly type ("3-assembly"/"5-assembly") — describes the
    // aircraft's landing gear architecture as a whole, so it lives here rather
    // than on any individual landing gear entry.
    assembly?: string | null;
    status: LeasingAircraftStatus;
    createdBy?: string | null;
    collectionId?: number | null;
    projects?: LeasingProject[];
    //TODO: Confirm, where does this data come from with Hasith
    operatingTimes?: OperatingTime[];
    engines?: AircraftEngine[];
    apu?: AircraftApu | null;
    landingGear?: AircraftLandingGearEntry[];
}

export interface ProjectMemberCreate {
    userId: number;
    role: string;
    orgType: string;
}

export interface ProjectMember {
    id: number;
    userId: number;
    role: string;
    orgType: string;
    name: string;
    username: string;
}

export interface LeasingProject {
    id: number;
    teamId: number;
    createdAt: string;
    leasingAircraft: LeasingAircraft;
    title: string;
    status: ProjectStatus; // defaults to ProjectStatus.InProgress on creation
    msn: string | null;
    aircraftType: string | null;
    endOfLeaseDate: string | null;
    deliveryDate: string | null;
    lessee: string | null;
    lessor?: string | null;
    leaseStartDate?: string | null;
    aircraftId: number | null;
    aircraft: AircraftSchema | null;
    members: ProjectMember[];
    projectType: ProjectType;
    tasks?: ProjectTask[];
    projectManager?: string | null;
    complianceSummary?: {
        totalChecks: number;
        passedChecks: number;
        progressPercentage: number;
    } | null;
}

export enum ProjectType {
    EndOfLease = 'end_of_lease',
    Aircraft = 'aircraft',
    Redelivery = 'redelivery',
    AircraftSale = 'aircraft_sale',
}

export function isWorkOrder(doc: AviationDocument) {
    return doc.type === 'WorkOrder';
}

export function isEngineeringOrder(doc: AviationDocument) {
    return doc.type === 'EngineeringOrder';
}

export function isTaskCard(doc: AviationDocument) {
    return doc.type === 'TaskCard';
}

export function isServiceBulletin(doc: AviationDocument) {
    return doc.type === 'ServiceBulletin';
}

export function isAirworthinessDirective(doc: AviationDocument) {
    return doc.type === 'AirworthinessDirective';
}

export type Activity = {
    username: string;
    title: string;
    description: string | null;
    type: 'upload' | 'status_change' | 'ai_check';
    createdAt: string;
};

export type DocumentStats = {
    category: AviationDocumentCategory;
    displayName: string;
    total: number;
    itemsOpen: number;
    itemsClosed: number;
    itemsPending: number;
};

interface ComponentMeta {
    partNumber?: string;
    mpdReferences?: string;
    ata?: string;
    nomenclature?: string;
    position?: string;
    interval?: Interval;
    threshold?: Interval;
    category?: ComponentCategory;
}

export interface ComponentItemMeta {
    serialNumber?: string;
    containerPartNumber?: string;
    containerSerialNumber?: string;
    componentTsn?: string;
    componentCsn?: string;
    work?: string;
    installDate?: string;
    // Date of manufacture as the status report printed it, so a month-only value
    // ("2005-06") stays month-only rather than being widened to a day.
    manufactureDate?: string;
    interval?: Interval;
    threshold?: Interval;
    acFhAtInstall?: string;
    acFcAtInstall?: string;
    componentFhAtInstall?: string;
    componentFcAtInstall?: string;
    componentFhAtLastDone?: string;
    componentFcAtLastDone?: string;
    lastDone?: DueInfo;
    nextDue?: DueInfo;
    remaining?: DueInfo;
    dueSymbol?: string;
    remark?: string;
    reportedDate?: string;
    offPartNumber?: string;
    offSerialNumber?: string;
    offDate?: string;
    removalReason?: string;
    qaStamp?: string;
    zoneFin?: string;
    ataChapter?: string;
}

export interface Component {
    id: number;
    partNumber: string | null;
    position: string | null;
    name: string;
    leasingProjectId: number;
    meta: ComponentMeta;
    category: ComponentCategory | null;
    maintenancePlanId?: number;
}

export type ComponentItemStatus = 'installed' | 'removed';

export interface ComponentItem {
    id: number;
    componentId: number;
    serialNumber: string | null;
    meta: ComponentItemMeta;
    status: ComponentItemStatus;
    // Only the per-component items endpoint computes this; the components list serves a
    // plain item.
    isContainerInformationMissing?: boolean;
}

export interface ComponentWithInstalledItem extends Component {
    installedItem: ComponentItem | null;
}

export interface DueInfo {
    date?: string;
    days?: string;
    fh?: string;
    fc?: string;
}

export interface Interval {
    mo?: string;
    ca?: string;
    fh?: string;
    fc?: string;
}

export interface MaintenancePlan {
    id: string;
    taskId: string;
    description: string;
    interval: Interval;
}

export interface MaintenanceTask {
    id: string;
    taskType: string;
    interval: Interval;
    performedDate: string;
    dueDate: string;
    partNumber: string | null;
    remainingDays: number;
    serialNumber: string | null;
    status: string;
    mpdReference?: string;
    reportedDate?: string;
}

export interface MaintenanceActivityRow {
    id: number;
    componentId: number;
    componentName: string;
    partNumber: string | null;
    serialNumber: string | null;
    taskType: string;
    interval: Interval | null;
    performedDate: string | null;
    dueDate: string | null;
    mpdReference: string | null;
    reportedDate: string | null;
    status: string;
    remainingDays: number;
    meta: ComponentItemMeta | null;
    aiInsight?: StatusReportAIInsight;
    reportDocId: number;
    reportName: string | null;
    reportTab: string | null;
    isContainerInformationMissing: boolean;
}

export interface TimelineData {
    title: string;
    status: 'complete' | 'warning' | 'error';
    details: Record<string, string>;
}

export interface DocumentComponent {
    title: string;
    type: string;
    date: string;
    size: string;
    warning?: boolean;
}

export interface HistoryData {
    action: string;
    date: string;
    user: string;
    details: string;
}

export interface FileUploadProgress {
    file: DBFile;
    ingestedFile: DBIngestedFile;
    subDocuments: Doc[];
}

export const FILE_TYPES = [
    { value: FileType.sb, label: 'Service Bulletin' },
    { value: FileType.ad, label: 'Airworthiness Directives' },
    // { value: FileType.contract, label: "Contracts" },
    { value: FileType.eo, label: 'Engineering Order' },
    { value: FileType.dr, label: 'Damage Reports' },
    { value: FileType.dir, label: 'Discrepancy Report' },
    { value: FileType.mpd, label: 'Maintenance Planning Document' },
    { value: FileType.sr, label: 'Status Report' },
    // { value: FileType.cmm, label: "Component Maintenance Manual" },
    { value: FileType.arc, label: 'Authorized Release Certificate' },
    { value: FileType.wo, label: 'Work Order' },
    { value: FileType.air, label: 'Aircraft Inspection Report' },
    { value: FileType.tc, label: 'Task Card' },
    { value: FileType.rdas, label: 'Repair Design Approval Sheet' },
    { value: FileType.ct, label: 'Component Tag' },
    { value: FileType.wr, label: 'Work Report' },
    { value: FileType.adtlb, label: 'Aircraft Delivery Technical Logbook' },
    { value: FileType.sv, label: 'Shop Visit Report' },
    { value: FileType.afm, label: 'Aircraft Flight Manual' },
    { value: FileType.amp, label: 'Aircraft Maintenance Program' },
];

export interface DocumentCategoryConfig {
    code: string;
    name: string;
    filter: (document: LeasingDocs) => boolean;
}

export type WorkOrderStatus = 'completed' | 'pending';

export type NRJCStatus = 'completed' | 'pending';

export type TLSStatus = 'completed' | 'pending';

export interface TLSPartRemoved {
    partNumber: string;
    serialNumber: string;
    reasonRemoved?: string | null;
}

export interface TLSPartInstalled {
    partNumber: string;
    serialNumber: string;
}

export interface TLSEntry {
    defectId: string;
    description?: string | null;
    action?: string | null;
    partRemoved?: TLSPartRemoved | null;
    partInstalled: TLSPartInstalled;
    status?: string | null;
    date?: string | null;
    engineerSignature?: string | null;
}

export interface TLSAircraft {
    registration: string;
}

export interface TLSDocument {
    type: string;
    ataReference?: string | null;
    component?: string | null;
}

export interface TechLogSheetData {
    type?: 'TechLogSheet';
    aircraft: TLSAircraft;
    document: TLSDocument;
    entries: TLSEntry[];
    maintenanceRelease?: {
        certificateStatement?: string | null;
        etops?: {
            '120'?: boolean | null;
            '180'?: boolean | null;
            '240'?: boolean | null;
        } | null;
    } | null;
}

export interface NRJCSignoff {
    signed: boolean;
    signedDate: string;
    staffNo?: string | null;
    name?: string | null;
    stampNo?: string | null;
    licenseNo?: string | null;
    remarks?: string | null;
}

export interface NRJCAircraft {
    registration: string;
    model: string;
    zone: string;
    ata: string;
    checkType?: string | null;
}

export interface NRJCWorkOrder {
    woNo: string;
    originatingCard: string;
    nrcType?: string | null;
    inspectionType?: string | null;
}

export interface NRJCFlags {
    PSE?: boolean | null;
    FCS?: boolean | null;
    Major_Repair?: boolean | null;
    Out_of_Manual?: boolean | null;
    Exterior_Repair?: boolean | null;
    NDT_Report?: boolean | null;
    Supply_Inspection?: boolean | null;
    CCP?: boolean | null;
}

export interface NRJCPart {
    partNumber: string;
    serialNumber: string;
    quantity?: number | null;
    unit?: string | null;
}

export interface NRJCComponentPart {
    description: string;
    position?: string | null;
    removed?: NRJCPart | null;
    installed?: NRJCPart | null;
}

export interface NRJCMaintenanceAction {
    step: number;
    workRequirement: string;
    reference?: string | null;
    manHours?: number | null;
    mechanicSignoff?: NRJCSignoff | null;
    inspectionSignoff?: NRJCSignoff | null;
}

export interface NRJCToolRow {
    item: number;
    partNo?: string | null;
    description?: string | null;
    quantity?: number | null;
    alternativePartNo?: string | null;
}

export interface NRJCMaterialRow {
    item: number;
    description?: string | null;
    partNo?: string | null;
    qtyDemanded?: number | null;
    unit?: string | null;
    pnActualUsed?: string | null;
    qtyUsedActually?: number | null;
    pnFrom?: string | null;
    batchNo?: string | null;
}

export interface NRJCMeteringToolRow {
    nameOfTool?: string | null;
    idNo?: string | null;
    calibrationDate?: string | null;
    dueDate?: string | null;
}

export interface NonRoutineJobCardData {
    type?: 'NonRoutineJobCard';
    documentType: string;
    company: string;
    nrcNumber: string;
    barcode?: string | null;
    raisedDate: string;
    customer: string;
    aircraft: NRJCAircraft;
    workOrder: NRJCWorkOrder;
    skill?: string | null;
    flags?: NRJCFlags | null;
    defectTask: { description: string };
    reference?: string | null;
    componentParts: NRJCComponentPart[];
    tools: NRJCToolRow[];
    materialUsed: NRJCMaterialRow[];
    meteringToolsRecord: NRJCMeteringToolRow[];
    maintenanceAction: NRJCMaintenanceAction[];
}

export type ComplianceCheckResult = {
    id: number;
    complianceStatusId: number;
    complianceCheckId: number;
    passed: boolean;
    message: string;
    createdAt: string;
    name: string;
    description: string;
    isMandatory: boolean;
};

export type ProjectDocComplianceFailure = ComplianceCheckResult & {
    targetId: number;
    docFilename: string | null;
    checkType: string | null;
};

export type CategoryComplianceStats = {
    category: AviationDocumentCategory;
    totalItems: number;
    totalChecks: number;
    passedChecks: number;
    failedChecks: number;
    progressPercentage: number;
    lastUpdated: string | null;
};

// Enum values match BE snake_case format
export enum ProjectTaskStatus {
    NotStarted = 'not_started',
    InProgress = 'in_progress',
    OnHold = 'on_hold',
    Completed = 'completed',
}

export enum ProjectTaskPriority {
    Low = 'low',
    Medium = 'medium',
    High = 'high',
}

export enum ProjectTaskParty {
    Lessor = 'lessor',
    CAMO = 'camo',
    MRO = 'mro',
}

export type ProjectTask = {
    id: number;
    leasingProjectId: number;
    teamId: number;
    assigneeId: number | null;
    name: string;
    startDate: string;
    endDate: string;
    status: ProjectTaskStatus;
    priority: ProjectTaskPriority;
    party: ProjectTaskParty;
    createdAt: string;
    updatedAt: string;
};

export type ProjectTaskCreate = {
    name: string;
    startDate: string;
    endDate: string;
    status: ProjectTaskStatus;
    priority: ProjectTaskPriority;
    assigneeId: number;
    party: ProjectTaskParty;
};

export type ProjectTaskUpdate = {
    status: ProjectTaskStatus;
    priority: ProjectTaskPriority;
    assigneeId: number;
    startDate: string;
    endDate: string;
};

// TODO: Add more folder letters for other folder!
export type FolderLetter = 'B' | 'C' | 'E' | 'F1' | 'F2' | 'H';

export interface FolderTypeConfig {
    id: number;
    title: string;
    description: string;
    contentType:
        | 'component'
        | 'docs'
        | 'ad-demo'
        | 'sb-demo'
        | 'ht-demo'
        | 'oc-demo'
        | 'btb-demo'
        | 'llp-demo'
        | 'llp2-demo'
        | 'none';
    apiCategory: string;
    // TODO: Optional since unsure if current BE infrastructure supports this
    // Make these required when fleshing out/wiring up subfolder APIs
    documentCount?: number;
    priority?: 'high' | 'medium' | 'low';
    lastUpdate?: string;
    progress?: number;
}

export interface FolderConfig {
    name: string;
    description: string;
    types: FolderTypeConfig[];
}

// AD Status Report types (from ADSR documents)

export interface ADStatusReportReference {
    type: string; // 'FAA AD' | 'EASA AD' | 'TCCA AD' | 'CAD' | 'OTHER'
    value: string;
}

export interface ADDocumentRef {
    code: string;
    docType: string;
    raw: string | null;
}

export interface ADMetric {
    value: string | null;
    unit: string | null;
    raw: string | null;
}

export interface ADCompliancePoint {
    date: string | null;
    fh: string | null;
    fc: string | null;
    rawDate: string | null;
    rawFh: string | null;
    rawFc: string | null;
}

export interface ADThreshold {
    date: string | null;
    fh: string | null;
    fc: string | null;
    durationMonths: number | null;
    raw: string | null;
    basis: string | null;
}

export interface ADApplicability {
    applies: boolean | null;
    scope: string | null;
    reason: string | null;
}

export interface ADComplianceAction {
    status: string | null;
    complianceDoc: ADDocumentRef;
    repetitiveInterval: ADMetric;
    threshold: ADThreshold | null;
    lastDone: ADCompliancePoint | null;
    nextDue: ADThreshold | null;
    remaining?: DueInfo | null;
    remarks: string | null;
    refersToHardTime: boolean;
}

export interface ADReferenceDocCompliance {
    doc: ADDocumentRef | null;
    complianceActions: ADComplianceAction[];
}

export interface ADStatusReportRow {
    rowNumber: string | null;
    ads: ADStatusReportReference[];
    supersedes: string[];
    supersededBy: string[];
    effectiveDateStr: string;
    effectiveDate: string | null;
    subject: string;
    applicability: ADApplicability;
    referenceDocs: ADReferenceDocCompliance[];
}

export interface ADStatusReportHeader {
    msn: string;
    date: string;
    aircraft: string | null;
    registration: string | null;
    fh: string | null;
    fc: string | null;
    apu: string | null;
    lineNumber: string | null;
}

/** How an AD row changed relative to the prior (superseded) report. ``removed``
 * is an AD the new report no longer lists, surfaced as a struck-through,
 * non-clickable ghost row (EOL-1896). */
export type ADStatusRowChangeStatus =
    | 'added'
    | 'modified'
    | 'unchanged'
    | 'removed';

/**
 * Prior values of the fields that changed on one compliance action (EOL-1896).
 * A key present here changed (the value is the *old* one); an absent key is
 * unchanged. `referenceDoc` / `complianceDoc` are the old rendered strings (main
 * table); `lastDone` / `nextDue` / `remaining` mirror `ADComplianceAction`'s
 * shapes for the row-detail highlight. All may themselves be `null` when the
 * prior action had no value.
 */
export interface ADStatusCompliancePrevious {
    complianceStatus?: string;
    referenceDoc?: string | null;
    complianceDoc?: string | null;
    lastDone?: ADCompliancePoint | null;
    nextDue?: ADThreshold | null;
    remaining?: DueInfo | null;
}

/**
 * Prior values for the fields that changed in the new report. Row-level fields
 * (`effectiveDate` / `supersededBy`) sit at the top; `compliances` is
 * index-aligned to the row's flattened compliance actions, each entry that
 * action's prior values or `null` when unchanged — so every sub-row highlights
 * its own change. Populated only when `highlightsActive`.
 */
export interface ADStatusRowPrevious {
    // `null` = prior report had no date (null → date change); still a change.
    effectiveDate?: string | null;
    supersededBy?: string[];
    compliances?: (ADStatusCompliancePrevious | null)[];
}

/** An AD that the prior report listed but the new report no longer does. */
export interface RemovedADNotice {
    ads: ADStatusReportReference[];
    subject: string | null;
}

export interface ADStatusRowWithContext
    extends ADStatusReportRow,
        ADStatusReportHeader {
    aiFindings: boolean;
    docId: number;
    rowIndex: number;
    matchedDocumentsCount: number;
    aiInsights?: StatusReportAIInsight;
    changeStatus?: ADStatusRowChangeStatus;
    previous?: ADStatusRowPrevious | null;
}

export interface ADStatusReportResponse {
    header: ADStatusReportHeader | null;
    rows: ADStatusRowWithContext[];
    totalRows: number;
    highlightsActive: boolean;
    removedAds: RemovedADNotice[];
}

// SB Status Report types (from SBSR documents)

export interface SBCompliancePoint {
    date: string | null;
    fh: string | null;
    fc: string | null;
}

export interface SBNextDue {
    interval: string | null;
    date: string | null;
    fh: string | null;
    fc: string | null;
}

export interface DocumentReference {
    docType: string;
    reference: string;
}

export interface SBStatusReportRow {
    sb: string;
    title: string;
    references: DocumentReference[];
    status: string | null;
    lastDone: SBCompliancePoint;
    nextDue: SBNextDue;
    note: string | null;
}

export interface SBStatusReportHeader {
    msn: string | null;
    date: string | null;
    aircraft: string | null;
    registration: string | null;
    variableNumber: string | null;
    fh: string | null;
    fc: string | null;
    apu: string | null;
    lineNumber: string | null;
    enginePn: string | null;
    engineType: string | null;
    esn: string | null;
}

export interface SBStatusRowWithContext
    extends SBStatusReportRow,
        SBStatusReportHeader {
    docId: number;
    rowIndex: number;
    aiFindings: boolean | null;
}

export interface SBStatusReportResponse {
    header: SBStatusReportHeader | null;
    rows: SBStatusRowWithContext[];
    totalRows: number;
}

// ─── AD/SB Record Types (moved from demo/mock-data for production use) ─────────

export const COMPLIANCE_STATUSES = [
    'Open',
    'Closed',
    'Not Applicable',
    'Terminated',
    'Repetitive',
    'Superseded',
    'Cancelled',
] as const;

export type ComplianceStatus = (typeof COMPLIANCE_STATUSES)[number];

export interface ComplianceWindow {
    date: string | null;
    fh: string | null;
    fc: string | null;
}

export interface ADIntervalInfo {
    duration: string | null;
    fh: string | null;
    fc: string | null;
}

export interface ADRecord {
    docId?: number;
    rowIndex?: number;
    // Position of this compliance action within the ADSR row's reference_docs →
    // compliance_actions, matching the LDND block key (refDocIndex, actionIndex).
    refDocIndex?: number;
    actionIndex?: number;
    euad: string;
    supersedes: string | null;
    faaAd: string | null;
    subject: string;
    effectiveDate: string | null;
    applicability: ADApplicability;
    referenceDoc: string | null;
    referenceDocType: string;
    complianceDoc: string;
    complianceDocType: string;
    lastDone: ComplianceWindow;
    interval?: ADIntervalInfo;
    nextDue: ComplianceWindow;
    remaining?: DueInfo;
    remarks: string;
    adClosed: boolean;
    complianceStatus: ComplianceStatus;
    aiFindings: boolean;
    aiInsights?: StatusReportAIInsight;
    source: 'mock' | 'pdf' | 'mock+pdf';
    isFirstCompliance: boolean;
    complianceIndex: number;
    totalCompliances: number;
    // Report-level context (flattened from the ADSR header)
    statusDate?: string | null;
    aircraftTsn?: string | null;
    aircraftCsn?: string | null;
    // Prior field values of *this* compliance action for change-highlighting
    // (EOL-1896) — `mapRowToADRecords` threads the row's per-action `previous`
    // entry onto each record, so every compliance row highlights its own change.
    previous?: ADStatusCompliancePrevious | null;
}

/**
 * One AD rendered as a single table row in the AD status report. Per-compliance fields
 * are nested under `compliances` so the row component (ADTableRow) can stack multiple
 * compliance entries vertically inside individual cells while AD-level cells span the
 * full row height. Distinct from the legacy flat `ADRecord` (one record per compliance),
 * which is still used by the detail view, demos, and the ad-compliance report.
 */
export interface ADTableRowWithCompliances
    extends Omit<
        ADRecord,
        | 'referenceDoc'
        | 'referenceDocType'
        | 'complianceDoc'
        | 'complianceDocType'
        | 'lastDone'
        | 'interval'
        | 'nextDue'
        | 'adClosed'
        | 'complianceStatus'
        | 'isFirstCompliance'
        | 'complianceIndex'
        | 'totalCompliances'
        | 'remarks'
        // `previous` here is the row-level snapshot (per-action lives under
        // `compliances`), not `ADRecord`'s single-action entry.
        | 'previous'
    > {
    ads: ADStatusReportReference[];
    compliances: ADTableRowComplianceItem[];
    supersededBy: string[];
    matchedDocumentsCount: number;
    changeStatus?: ADStatusRowChangeStatus;
    previous?: ADStatusRowPrevious | null;
}

export type ADTableRowComplianceItem = Pick<
    ADRecord,
    | 'referenceDoc'
    | 'referenceDocType'
    | 'complianceDoc'
    | 'complianceDocType'
    | 'complianceStatus'
    | 'lastDone'
    | 'interval'
    | 'nextDue'
    | 'adClosed'
>;

export interface SBComplianceInfo {
    date: string | null;
    tsn: string | null;
    csn: string | null;
}

export interface SBNextDueInfo {
    interval: string | null;
    date: string | null;
    hours: string | null;
}

export interface SBRecord {
    id: string;
    sbNo: string;
    rev: string;
    subject: string;
    relatedAD: string | null;
    accomplished: string;
    status:
        | 'Closed'
        | 'Open'
        | 'In Progress'
        | 'Not Applicable'
        | 'Repetitive'
        | 'Terminated'
        | 'Superseded'
        | 'Cancelled';
    aiFindings: boolean;
    estimatedManHours: number | null;
    actualManHours: number | null;
    lastDone?: SBComplianceInfo;
    nextDue?: SBNextDueInfo;
}

export interface DocumentStatItem {
    folder: string;
    subfolder: string | null;
    count: number;
}

export interface DocumentStatsResponse {
    items: DocumentStatItem[];
    total: number;
}

export interface SkippedTab {
    sheetName: string;
    // 'hidden' | 'unrecognised' | 'wrong_aircraft' are verdicts about the tab's
    // contents. 'extraction_failed' means extraction never completed, so the tab's
    // contents are unknown — it must not be described as an unsupported format.
    reason: 'hidden' | 'unrecognised' | 'wrong_aircraft' | 'extraction_failed';
}

// Reports ingested before the ETL stamped sheet_type read 'other'.
export type SheetType = 'oxygen_generator' | 'life_vest' | 'other';

export interface ReportSummary {
    aircraftTsn: string | null;
    aircraftCsn: string | null;
    statusReportDate: string | null;
    reportDocId: number | null;
    reportUrl: string | null;
    category: string | null;
    reportName: string | null;
    fileName: string | null;
    utilisation: string | null;
    reportTab: string | null;
    skippedTabs: SkippedTab[];
    sheetType: SheetType;
    hasContainerColumns: boolean;
    hasManufactureDateColumn: boolean;
    isContainerInformationMissing: boolean;
}
